import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useFetch } from "@/lib/useFetch";
import { mediaUrl } from "@/lib/api";
import { Seo, breadcrumbLd } from "@/components/site/Seo";
import { PageHero, CtaBand, ServiceCard } from "@/components/site/Common";
import { Sections, RichText } from "@/components/site/RichText";
import NotFound from "@/pages/NotFound";

export function Loading() {
  return (
    <div className="container-x py-24" data-testid="page-loading">
      <div className="h-8 w-1/2 animate-pulse rounded bg-zinc-200" />
      <div className="mt-6 h-4 w-3/4 animate-pulse rounded bg-zinc-100" />
      <div className="mt-3 h-4 w-2/3 animate-pulse rounded bg-zinc-100" />
    </div>
  );
}

export function InfoPage({ pageKey, image, children }) {
  const { data, loading, error } = useFetch(`/public/pages/${pageKey}`);
  if (loading) return <Loading />;
  if (error || !data) return <NotFound />;
  const path = `/${pageKey}`;
  return (
    <>
      <Seo title={data.seo_title} description={data.seo_description} path={path} jsonLd={breadcrumbLd([{ name: "Ana Sayfa", path: "/" }, { name: data.title, path }])} />
      <PageHero title={data.title} image={image} crumbs={[{ name: data.title }]} eyebrow={data.legal ? "Hukuki Bilgilendirme" : undefined} />
      <div className="container-x py-14 sm:py-20">
        <div className={children ? "grid gap-12 lg:grid-cols-[1fr_420px]" : "max-w-3xl"}>
          <div>
            {data.legal && <p className="mb-8 rounded border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500">Son güncelleme: sitenin yayına alındığı tarih. Bu metin bilgilendirme amaçlıdır.</p>}
            <Sections sections={data.sections} />
          </div>
          {children}
        </div>
      </div>
      {!data.legal && <CtaBand />}
    </>
  );
}

export function About() {
  const { settings: s } = useSite();
  return (
    <InfoPage pageKey="hakkimizda" image="/img/kusadasi.webp">
      <div className="space-y-6">
        <figure className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="about-values-image">
          <img src={mediaUrl(s.values_image)} alt="Kurumsal değerlerimiz: Öngörülü, Rekabetçi, Çalışkan, Dürüst, Güvenilir" loading="lazy" className="w-full" />
          <figcaption className="mt-2 text-center text-xs text-zinc-500">Kurumsal değerlerimiz</figcaption>
        </figure>
        <div className="rounded-lg bg-[color:var(--ink)] p-6 text-sm text-zinc-300">
          <p className="font-bold text-white">{s.legal_name}</p>
          <p className="mt-2">Kuruluş: {s.founded}</p>
          <p className="mt-1">{s.address}</p>
        </div>
      </div>
    </InfoPage>
  );
}

const SERVICES_CRUMBS = [{ name: "Hizmetlerimiz" }];
const SECTORS_CRUMBS = [{ name: "Sektörler" }];

export function ServicesList() {
  const { settings: s, services } = useSite();
  return (
    <>
      <Seo title={s.seo?.services?.title} description={s.seo?.services?.description} path="/hizmetlerimiz" />
      <PageHero title="Hizmetlerimiz" subtitle="Her tesisin yapısına ve ihtiyacına göre planlanan özel güvenlik hizmetleri." image="/img/checkpoint.webp" crumbs={SERVICES_CRUMBS} />
      <section className="bg-zinc-50 py-14 sm:py-20">
        <div className="container-x grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {services.map((sv, i) => <ServiceCard key={sv.slug} item={sv} i={i} />)}
        </div>
      </section>
      <CtaBand />
    </>
  );
}

export function SectorsList() {
  const { settings: s, sectors } = useSite();
  return (
    <>
      <Seo title={s.seo?.sectors?.title} description={s.seo?.sectors?.description} path="/sektorler" />
      <PageHero title="Sektörler ve Bölgeler" subtitle="Kuşadası, Aydın ve Ege Bölgesi'ndeki farklı tesis türleri için hazırlanmış rehber sayfalar." image="/img/aegean.webp" crumbs={SECTORS_CRUMBS} />
      <section className="bg-zinc-50 py-14 sm:py-20">
        <div className="container-x">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="sectors-grid">
            {sectors.map((sc) => <ServiceCard key={sc.slug} item={sc} />)}
          </div>
          <div className="mt-14 max-w-3xl rounded-lg border border-zinc-200 bg-white p-6" data-testid="service-regions">
            <h2 className="text-lg font-bold text-zinc-950">Hizmet bölgesi</h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {(s.service_regions || []).map((r) => (
                <li key={r} className="rounded-full border border-zinc-300 px-3 py-1 text-sm text-zinc-700">{r}</li>
              ))}
            </ul>
            <RichText text={s.service_regions_note} className="mt-4 text-sm" />
            <Link to="/teklif-al" className="btn-red mt-2">Bölgeniz için teklif isteyin</Link>
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
